import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { SITE_URL } from "@/lib/seo";
import api from "@/lib/api";
import FlagSelect from "@/components/FlagSelect";
import { SCHENGEN_CODES, VISA_FREE_CODES } from "@/lib/worldCountries";
import {
  ArrowRight, ChevronRight, FileText, DollarSign, Camera,
  Clock, MapPin, Info, CheckCircle2, X, ExternalLink,
} from "lucide-react";
import "flag-icons/css/flag-icons.min.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const TABS = [
  { id: "overview", label: "Overview", icon: Info },
  { id: "fees", label: "Visa Fees", icon: DollarSign },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "photo", label: "Photo", icon: Camera },
  { id: "processing", label: "Processing", icon: Clock },
  { id: "where_to_apply", label: "Where to Apply", icon: MapPin },
];

export default function GuideCombination() {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Route: /schengen-visa-guide/:destination/from-:nationality  OR  /schengen-visa-guide/:destination
  const destParam = params.destination || "";
  const natParam = (params.nationality || "").replace(/^from-/, "");
  const residenceParam = (searchParams.get("residence") || "").toUpperCase();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [nationalities, setNationalities] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [countries, setCountries] = useState([]);           // ISO country list (residence picker)
  const [visaTypes, setVisaTypes] = useState([]);            // all globally-active types
  const [selectedVisaType, setSelectedVisaType] = useState("tourist");
  const [residence, setResidence] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [showBanner, setShowBanner] = useState(true);
  // When we transparently fall back from an unpublished combo to the
  // destination-only page, keep a small dismissible notice so the user
  // understands why the H1 changed. Preserved across the URL-replace by
  // NOT resetting it in the load useEffect — only cleared on dismiss or
  // when the user picks a new destination/nationality via the selectors.
  const [fallbackNotice, setFallbackNotice] = useState(null);
  const tabsRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setNotFound(false);
    setData(null);
    // Guard against stale responses: if the user rapidly switches selectors
    // we must not commit an old fetch's result over a newer request.
    let ignore = false;

    // Helper: fetch + set destination-only data (fallback when a
    // nationality+destination combo isn't published).
    const loadDestinationOnly = async () => {
      const res = await api.get(`/public/guide/${destParam.toUpperCase()}`);
      if (ignore) return null;
      const d = res.data.destination;
      const payload = {
        destination: { code: d.code, name: d.name, flag_code: d.flag_code || d.code, slug: d.slug },
        nationality: null,
        base_content: d.base_content || {},
        overrides: {},
        visa_type_overrides: d.visa_type_overrides || {},
        available_visa_types: ["tourist", ...Object.keys(d.visa_type_overrides || {})],
        where_to_apply_by_residence: d.where_to_apply_by_residence || [],
        faqs: (d.base_content || {}).faqs || [],
        appointment_url: null,
        meta_title: d.meta_title,
        meta_description: d.meta_description,
        last_updated: d.updated_at,
        status: "published",
        visa_type: "tourist",
      };
      setData(payload);
      return payload;
    };

    const load = async () => {
      try {
        const [nres, dres, vres, cres] = await Promise.all([
          api.get("/public/guide/nationalities"),
          api.get("/public/guide"),
          api.get("/public/visa-types").catch(() => ({ data: { visa_types: [] } })),
          api.get("/public/countries").catch(() => ({ data: { countries: [] } })),
        ]);
        if (ignore) return;
        setNationalities(nres.data.nationalities || []);
        setDestinations(dres.data.destinations || []);
        setVisaTypes(vres.data.visa_types || []);
        setCountries(cres.data.countries || nres.data.nationalities || []);
        if (natParam) {
          try {
            const res = await api.get(`/public/guide/${destParam.toUpperCase()}/${natParam.toUpperCase()}`);
            if (ignore) return;
            setData(res.data);
          } catch (comboErr) {
            // Graceful fallback: no published combination for this nat+dest.
            // Fall through to the destination-only page instead of a 404 dead-end,
            // and update the URL so the user shares/reloads the right canonical.
            if (ignore) return;
            const payload = await loadDestinationOnly();
            if (ignore || !payload) return;
            // Resolve a friendly nationality label for the notice. Prefer the
            // demonym from /public/countries (e.g. "Chinese") over the plain
            // country name from /public/guide/nationalities (e.g. "China").
            const natLc = natParam.toLowerCase();
            const natRec = (nres.data.nationalities || []).find((n) => {
              const s = (n.slug || "").toLowerCase();
              const c = (n.code || "").toLowerCase();
              return s === natLc || c === natLc;
            });
            const countryRec = (cres.data.countries || []).find((c) => {
              const cc = (c.code || "").toLowerCase();
              return cc === natLc || cc === (natRec?.code || "").toLowerCase();
            });
            const natLabel = countryRec?.demonym || natRec?.demonym || natRec?.name || natParam;
            setFallbackNotice({ natLabel, destName: payload.destination.name });
            const dSlug = payload.destination.slug || destParam.toLowerCase();
            const qs = searchParams.toString();
            const suffix = qs ? `?${qs}` : "";
            navigate(`/schengen-visa-guide/${dSlug}${suffix}`, { replace: true });
          }
        } else {
          // Destination-only page: fetch destination directly.
          await loadDestinationOnly();
        }
      } catch (e) {
        if (!ignore) setNotFound(true);
      }
    };
    load();
    return () => { ignore = true; };
  }, [destParam, natParam, navigate, searchParams]);

  // Residence defaults to the ?residence= query param, else to the nationality
  // (users applying from their home country are the common case), else empty.
  useEffect(() => {
    if (residenceParam) {
      setResidence(residenceParam);
    } else if (data?.nationality?.code) {
      setResidence(data.nationality.code);
    }
  }, [residenceParam, data?.nationality?.code]);

  const onSwitchResidence = (code) => {
    setResidence(code);
    const params = new URLSearchParams(searchParams);
    if (code) params.set("residence", code); else params.delete("residence");
    setSearchParams(params, { replace: true });
  };

  // Legacy code URL → slug URL redirect (client-side). Also handles case where
  // one segment is a code and the other is a slug. Uses replace() so bots see
  // the canonical <link rel="canonical"> we set via <SEO/> even if they don't
  // execute JS — but the redirect makes sure human clicks work correctly.
  useEffect(() => {
    if (!data) return;
    const dSlug = data.destination?.slug;
    const nSlug = data.nationality?.slug;
    const dCode = data.destination?.code;
    const nCode = data.nationality?.code;
    // IMPORTANT: `data` may still hold a *previous* fetch when the user
    // rapidly switches selectors. Only redirect once the loaded data
    // actually corresponds to the current URL — otherwise we'd navigate
    // the user back to the old slug and skeleton-loop them.
    const destParamLc = destParam.toLowerCase();
    const natParamLc = natParam.toLowerCase();
    const destIsFresh =
      (dSlug && destParamLc === dSlug.toLowerCase()) ||
      (dCode && destParamLc === dCode.toLowerCase());
    const natIsFresh =
      !natParam ||
      (nSlug && natParamLc === nSlug.toLowerCase()) ||
      (nCode && natParamLc === nCode.toLowerCase());
    if (!destIsFresh || !natIsFresh) return;

    const wantsDest = dSlug && destParam.toLowerCase() !== dSlug.toLowerCase();
    const wantsNat = natParam && nSlug && natParam.toLowerCase() !== nSlug.toLowerCase();
    if (wantsDest || wantsNat) {
      const qs = searchParams.toString();
      const suffix = qs ? `?${qs}` : "";
      const target = natParam && nSlug
        ? `/schengen-visa-guide/${dSlug}/from-${nSlug}${suffix}`
        : `/schengen-visa-guide/${dSlug}${suffix}`;
      navigate(target, { replace: true });
    }
  }, [data, destParam, natParam, navigate, searchParams]);

  const merged = useMemo(() => {
    if (!data) return null;
    const base = data.base_content || {};
    const ov = data.overrides || {};
    // Layer per-visa-type overrides on top of base + tourist overrides. For
    // the default (tourist) type, no extra layer is applied — Tourist content
    // lives in base + overrides for backwards compatibility.
    const vto = (data.visa_type_overrides || {})[selectedVisaType] || {};
    return {
      overview: mergeSection(mergeSection(base.overview, ov.overview), vto.overview),
      fees: mergeSection(mergeSection(base.fees, ov.fees), vto.fees),
      documents: mergeDocuments(mergeDocuments(base.documents, ov.documents), vto.documents),
      photo: mergeSection(mergeSection(base.photo, ov.photo), vto.photo),
      processing: mergeSection(mergeSection(base.processing, ov.processing), vto.processing),
      where_to_apply: mergeSection(mergeSection(base.where_to_apply, ov.where_to_apply), vto.where_to_apply),
    };
  }, [data, selectedVisaType]);

  // Available visa types on this page: tourist is always available; other
  // types only appear if they have non-empty content on this destination /
  // combination — so we never show an empty tab.
  const availableVisaTypes = useMemo(() => {
    if (!data) return [{ key: "tourist", name: "Tourist / Short Stay" }];
    const global = new Map(visaTypes.map((v) => [v.key, v.name]));
    const available = new Set(["tourist", ...(data.available_visa_types || [])]);
    // Preserve global order (Tourist first)
    const ordered = [];
    for (const v of visaTypes) if (available.has(v.key)) ordered.push({ key: v.key, name: v.name });
    // Fallback if visa_types API failed but combo has non-tourist content
    if (ordered.length === 0) {
      for (const key of available) ordered.push({ key, name: global.get(key) || key });
    }
    return ordered;
  }, [data, visaTypes]);

  // Where-to-apply entry for the currently selected residence country.
  const wtaForResidence = useMemo(() => {
    if (!data || !residence) return null;
    const entries = data.where_to_apply_by_residence || [];
    return entries.find((e) => (e.residence_country_code || "").toUpperCase() === residence.toUpperCase()) || null;
  }, [data, residence]);

  const residenceName = useMemo(() => {
    if (!residence) return "";
    // Prefer the broader ISO countries list; fall back to nationalities;
    // last-ditch: return the code so we never surface a raw ISO code
    // like "US" in customer copy accidentally — but hide the badge when
    // resolution fails via the ??.
    const c = countries.find((x) => x.code === residence);
    if (c) return c.name;
    const n = nationalities.find((x) => x.code === residence);
    return n?.name || residence;
  }, [residence, countries, nationalities]);

  // Residence options: full ISO country list MINUS Schengen countries (a
  // Schengen resident applies through their local channel, not the guide).
  const residenceOptions = useMemo(() => {
    if (!countries.length) return nationalities;
    return countries.filter((c) => !SCHENGEN_CODES.has(c.code));
  }, [countries, nationalities]);

  // Visa-exempt short-circuit — if the selected nationality can enter the
  // Schengen area visa-free for 90 days, show a friendly note instead of the
  // standard eligibility banner (matches the Build-Your-File UX).
  const isVisaExempt = useMemo(() => {
    const code = data?.nationality?.code;
    return code ? VISA_FREE_CODES.has(code) : false;
  }, [data?.nationality?.code]);

  const faqs = data?.faqs || [];

  // Sticky tab scroll spy
  useEffect(() => {
    const handler = () => {
      const els = TABS.map((t) => document.getElementById(`tab-${t.id}`)).filter(Boolean);
      const scrollY = window.scrollY + 200;
      let current = TABS[0].id;
      for (const el of els) {
        if (el.offsetTop <= scrollY) current = el.id.replace("tab-", "");
      }
      setActiveTab(current);
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, [data]);

  const scrollTo = (id) => {
    const el = document.getElementById(`tab-${id}`);
    if (el) window.scrollTo({ top: el.offsetTop - 130, behavior: "smooth" });
  };

  const buildQuery = () => (residence ? `?residence=${residence}` : "");

  const onSwitchNat = (code) => {
    if (!code) return;
    setFallbackNotice(null);
    const nObj = nationalities.find((n) => n.code === code);
    const dSlug = data?.destination?.slug || destParam.toLowerCase();
    const nSeg = nObj?.slug || code.toLowerCase();
    navigate(`/schengen-visa-guide/${dSlug}/from-${nSeg}${buildQuery()}`);
  };
  const onSwitchDest = (code) => {
    if (!code) return;
    setFallbackNotice(null);
    const dObj = destinations.find((d) => d.code === code);
    const dSlug = dObj?.slug || code.toLowerCase();
    const nObj = nationalities.find((n) => n.code === data?.nationality?.code);
    const nSlug = nObj?.slug || (natParam || "").toLowerCase();
    if (natParam) navigate(`/schengen-visa-guide/${dSlug}/from-${nSlug}${buildQuery()}`);
    else navigate(`/schengen-visa-guide/${dSlug}${buildQuery()}`);
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display font-bold text-3xl text-[#0f1b2d]">Guide not found</h1>
          <p className="mt-3 text-[#475569]">This nationality + destination guide isn&apos;t published yet.</p>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => { setNotFound(false); setData(null); navigate(0); }}
              data-testid="guide-not-found-retry"
              className="inline-flex items-center gap-2 border-2 border-[#2563eb] text-[#2563eb] hover:bg-[#eff6ff] font-semibold px-4 py-2 rounded-md text-sm"
            >
              Try again
            </button>
            <Link to="/schengen-visa-guide" data-testid="guide-not-found-back" className="inline-flex items-center gap-2 text-[#2563eb] font-semibold">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to Schengen Visa Guide
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!data || !merged) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-24">
          <div className="h-8 w-64 bg-[#f1f5f9] rounded animate-pulse" />
          <div className="mt-4 h-12 w-full bg-[#f1f5f9] rounded animate-pulse" />
          <div className="mt-8 h-96 w-full bg-[#f1f5f9] rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const destName = data.destination.name;
  const natName = data.nationality?.name;
  const dSlug = data.destination.slug || destParam.toLowerCase();
  const nSlug = data.nationality?.slug || natParam.toLowerCase();
  const h1 = natName
    ? `${destName} Schengen Visa for ${natName} Citizens — Requirements, Fees & Processing Time`
    : `${destName} Schengen Visa Guide — Requirements, Fees & Processing Time`;
  const seoTitle = data.meta_title || `${destName}${natName ? ` Schengen Visa for ${natName} Citizens` : " Schengen Visa Guide"} (2026): Fees, Documents, Processing Time | SchengenFile`;
  const seoDesc = data.meta_description || `Complete ${destName} Schengen visa guide${natName ? ` for ${natName} citizens` : ""}: required documents, fees, processing time and where to apply.`;

  // Related guides (up to 3 other destinations)
  const related = destinations.filter((d) => d.code !== data.destination.code).slice(0, 3);

  // Structured data
  const canonicalPath = natName
    ? `/schengen-visa-guide/${dSlug}/from-${nSlug}`
    : `/schengen-visa-guide/${dSlug}`;
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Schengen Visa Guide", item: `${SITE_URL}/schengen-visa-guide` },
      { "@type": "ListItem", position: 3, name: destName, item: `${SITE_URL}/schengen-visa-guide/${dSlug}` },
      ...(natName ? [{ "@type": "ListItem", position: 4, name: `For ${natName} Citizens`, item: `${SITE_URL}${canonicalPath}` }] : []),
    ],
  };
  const faqLd = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null;

  return (
    <div className="min-h-screen bg-white text-[#0f1b2d]" data-testid="guide-combination-page">
      <SEO title={seoTitle} description={seoDesc} path={canonicalPath} jsonLd={faqLd ? [breadcrumbLd, faqLd] : [breadcrumbLd]} />
      <Header />
      <main>
        {/* Header area */}
        <section className="pt-8 pb-4 border-b border-[#e2e8f0]">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            {/* Graceful-fallback notice: rendered only when the user was
                transparently redirected from an unpublished combination to the
                destination-only page. Dismissible. */}
            {fallbackNotice && !natName && (
              <div
                className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3"
                data-testid="guide-fallback-notice"
              >
                <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900 leading-relaxed flex-1">
                  No dedicated guide for <span className="font-semibold">{fallbackNotice.natLabel}</span> citizens applying to <span className="font-semibold">{fallbackNotice.destName}</span> yet — showing the general {fallbackNotice.destName} guide.
                </div>
                <button
                  type="button"
                  onClick={() => setFallbackNotice(null)}
                  aria-label="Dismiss"
                  data-testid="guide-fallback-notice-dismiss"
                  className="text-blue-700/70 hover:text-blue-900 shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            {/* Breadcrumb */}
            <nav className="text-xs text-[#64748b] flex items-center gap-1.5 flex-wrap" data-testid="guide-breadcrumb">
              <Link to="/" className="hover:text-[#0f1b2d]">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/schengen-visa-guide" className="hover:text-[#0f1b2d]">Schengen Visa Guide</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to={`/schengen-visa-guide/${dSlug}`} className="hover:text-[#0f1b2d]">{destName}</Link>
              {natName && (<>
                <ChevronRight className="w-3 h-3" />
                <span className="text-[#0f1b2d]">For {natName} Citizens</span>
              </>)}
            </nav>

            <div className="mt-4 flex items-center gap-3">
              <span className={`fi fi-${(data.destination.flag_code || data.destination.code).toLowerCase()} !w-12 !h-9 rounded shadow-sm`} aria-hidden />
              <div>
                <div className="text-xs uppercase tracking-widest text-[#2563eb] font-semibold">
                  {natName ? `${natName} → ${destName}` : `${destName} Schengen Visa`}
                </div>
                <h1 className="mt-1 font-display font-bold text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="guide-h1">{h1}</h1>
              </div>
            </div>

            {/* Selectors — mirror hub */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="guide-selectors">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b] block mb-1" data-testid="guide-selector-label-nationality">
                  What is your nationality?
                </label>
                <FlagSelect
                  testId="guide-selector-nationality"
                  ariaLabel="What is your nationality?"
                  placeholder="— Select nationality —"
                  value={data.nationality?.code || ""}
                  onChange={onSwitchNat}
                  options={nationalities}
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b] block mb-1" data-testid="guide-selector-label-residence">
                  Applying from?
                </label>
                <FlagSelect
                  testId="guide-selector-residence"
                  ariaLabel="Applying from"
                  placeholder="— Country of residence —"
                  value={residence}
                  onChange={onSwitchResidence}
                  options={residenceOptions}
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b] block mb-1" data-testid="guide-selector-label-destination">
                  Travelling to?
                </label>
                <FlagSelect
                  testId="guide-selector-destination"
                  ariaLabel="Travelling to"
                  placeholder="— Select destination —"
                  value={data.destination.code}
                  onChange={onSwitchDest}
                  options={destinations}
                />
              </div>
            </div>

            {/* Visa-exempt short-circuit: for nationalities that can enter
                the Schengen area visa-free, show a friendly note in place
                of the standard eligibility banner. */}
            {isVisaExempt && (
              <div
                className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3"
                data-testid="guide-visa-free-note"
              >
                <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900 leading-relaxed">
                  <span className="font-semibold">{data.nationality.demonym || data.nationality.name} citizens don&apos;t need a Schengen visa</span> for stays up to 90 days within any 180-day period. This guide is kept for reference, but you can travel to <span className="font-semibold">{destName}</span> with just your passport and any required entry documents.
                </div>
              </div>
            )}

            {/* Eligibility banner — personalises the page for the user's
                nationality + residence combination. Same visual as the green
                banner in Build Your File. Uses the demonym (e.g. 'Indian
                national') and the full country name for residence. Hidden
                when the visitor is visa-exempt (the note above replaces it). */}
            {!isVisaExempt && data.nationality && residence && (
              <div
                className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3"
                data-testid="guide-eligibility-banner"
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900 leading-relaxed">
                  <span className="font-semibold">{data.nationality.demonym || `${data.nationality.name}`} national</span>
                  <span> · Applying from </span>
                  <span className="font-semibold">{residenceName || residence}</span>
                  <span> → </span>
                  {wtaForResidence ? (
                    <>Apply at the <span className="font-semibold">{destName}</span> visa application centre via <span className="font-semibold">{wtaForResidence.provider}</span> in <span className="font-semibold">{residenceName || residence}</span>.</>
                  ) : (
                    <>Apply at the <span className="font-semibold">{destName}</span> consulate or its authorised visa centre in <span className="font-semibold">{residenceName || residence}</span>.</>
                  )}
                </div>
              </div>
            )}

            {/* Visa-type selector — functional. Only renders if the page has
                more than the default Tourist type of content. Switching swaps
                tab content client-side without a page reload. */}
            {availableVisaTypes.length > 1 && (
              <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="guide-visa-type-selector">
                <span className="text-xs uppercase tracking-widest text-[#64748b] font-semibold mr-1">Visa type</span>
                {availableVisaTypes.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => setSelectedVisaType(v.key)}
                    data-testid={`guide-visa-type-${v.key}`}
                    className={`text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full border transition ${
                      selectedVisaType === v.key
                        ? "bg-[#2563eb] text-white border-[#2563eb]"
                        : "bg-white text-[#0f1b2d] border-[#e2e8f0] hover:border-[#2563eb]"
                    }`}
                  >
                    {v.name}
                  </button>
                ))}
              </div>
            )}

            {/* Destination-level per-nationality rule (info box, e.g. "Russian
                nationals get single-entry visas with 45-day processing"). */}
            {!isVisaExempt && data.nationality_rule && (
              <div
                className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
                data-testid="guide-nationality-rule"
              >
                <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900 leading-relaxed">
                  <span className="font-semibold">Special rule for {data.nationality.demonym || data.nationality.name} nationals:</span>{" "}
                  {data.nationality_rule}
                </div>
              </div>
            )}

            {/* Combination-level free-form nationality note. */}
            {!isVisaExempt && data.nationality_notes && (
              <div
                className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 leading-relaxed"
                data-testid="guide-nationality-notes"
              >
                {data.nationality_notes}
              </div>
            )}

            <div className="mt-3 text-xs text-[#64748b]" data-testid="guide-last-updated">
              Last updated: {formatDate(data.last_updated)}
            </div>
          </div>
        </section>

        {/* Sticky tabs */}
        <div ref={tabsRef} className="sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-[#e2e8f0]" data-testid="guide-tabs">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 overflow-x-auto">
            <div className="flex items-center gap-1 py-2 min-w-max">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => scrollTo(t.id)}
                  data-testid={`guide-tab-${t.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-semibold rounded-md whitespace-nowrap transition ${
                    activeTab === t.id ? "bg-[#2563eb] text-white" : "text-[#475569] hover:bg-[#f1f5f9]"
                  }`}
                >
                  <t.icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tabbed content sections */}
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-10 space-y-14">
          <TabOverview id="tab-overview" data={merged.overview} destName={destName} natName={natName} />
          <TabFees id="tab-fees" data={merged.fees} />
          <TabDocuments id="tab-documents" data={merged.documents} destName={destName} />
          <TabPhoto id="tab-photo" data={merged.photo} />
          <TabProcessing id="tab-processing" data={merged.processing} />
          <TabWhereToApply id="tab-where_to_apply" data={merged.where_to_apply} appointmentUrl={data.appointment_url} residenceEntry={wtaForResidence} residenceName={residenceName} destinationName={destName} residenceSelected={!!residence} />
        </div>

        {/* FAQ */}
        {faqs.length > 0 && (
          <section className="py-14 bg-[#f8fafc] border-t border-[#e2e8f0]">
            <div className="max-w-3xl mx-auto px-6 lg:px-8">
              <div className="text-xs uppercase tracking-widest text-[#2563eb] font-semibold">FAQ</div>
              <h2 className="mt-1 font-display font-bold text-2xl sm:text-3xl text-[#0f1b2d]">Frequently asked questions</h2>
              <div className="mt-6 space-y-3" data-testid="guide-faqs">
                {faqs.map((f, i) => (
                  <details key={i} className="group border border-[#e2e8f0] rounded-lg bg-white">
                    <summary className="cursor-pointer list-none px-4 py-3 font-semibold text-[#0f1b2d] flex items-center justify-between">
                      <span>{f.q}</span>
                      <span className="text-[#64748b] group-open:rotate-180 transition">▾</span>
                    </summary>
                    <div className="px-4 pb-4 text-sm text-[#475569] leading-relaxed">{f.a}</div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* How SchengenFile helps */}
        <section className="py-16 border-t border-[#e2e8f0]">
          <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
            <div className="text-xs uppercase tracking-widest text-[#2563eb] font-semibold">How SchengenFile helps</div>
            <h2 className="mt-1 font-display font-bold text-2xl sm:text-3xl text-[#0f1b2d]">Complete document packages in 24 hours</h2>
            <p className="mt-3 text-[#475569]">Flight PNRs, hotel bookings, cover letters, insurance certificates — everything the consulate asks for, in one order.</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { name: "Basic", price: 39, bullets: ["Flight PNR", "Hotel booking", "Application form"] },
                { name: "Complete", price: 69, bullets: ["Basic + Cover letter", "Car rental", "Most popular"], popular: true },
                { name: "Premium", price: 99, bullets: ["Complete + Insurance", "Itinerary", "24/7 support"] },
              ].map((p) => (
                <div key={p.name} className={`rounded-xl border-2 p-5 text-left ${p.popular ? "border-[#2563eb] bg-[#eff6ff]" : "border-[#e2e8f0] bg-white"}`}>
                  <div className="text-xs uppercase tracking-wider text-[#64748b] font-semibold">{p.name}</div>
                  <div className="mt-1 font-display font-black text-3xl text-[#0f1b2d]">${p.price}</div>
                  <ul className="mt-3 text-sm text-[#475569] space-y-1">
                    {p.bullets.map((b) => <li key={b} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#16a34a] mt-0.5" />{b}</li>)}
                  </ul>
                </div>
              ))}
            </div>
            <Link
              to={`/apply?destination=${encodeURIComponent(destName)}`}
              data-testid="guide-cta-apply"
              className="mt-8 inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-6 py-3 rounded-md transition"
            >
              Start your {destName} visa file <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Related guides */}
        {related.length > 0 && (
          <section className="py-12 bg-[#f8fafc] border-t border-[#e2e8f0]">
            <div className="max-w-5xl mx-auto px-6 lg:px-8">
              <div className="text-xs uppercase tracking-widest text-[#2563eb] font-semibold">Also planning?</div>
              <h3 className="mt-1 font-display font-bold text-xl text-[#0f1b2d]">Related Schengen guides</h3>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {related.map((r) => (
                  <Link
                    key={r.code}
                    to={natName
                      ? `/schengen-visa-guide/${r.slug || r.code.toLowerCase()}/from-${nSlug}`
                      : `/schengen-visa-guide/${r.slug || r.code.toLowerCase()}`}
                    data-testid={`guide-related-${r.code.toLowerCase()}`}
                    className="rounded-lg border border-[#e2e8f0] bg-white p-4 hover:border-[#2563eb] transition flex items-center gap-3"
                  >
                    <span className={`fi fi-${(r.flag_code || r.code).toLowerCase()} !w-8 !h-6 rounded-sm shadow-sm`} aria-hidden />
                    <div>
                      <div className="text-sm font-semibold text-[#0f1b2d]">{r.name}</div>
                      <div className="text-xs text-[#64748b]">{natName ? `Guide for ${natName} citizens` : "View guide"}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Sticky bottom bar */}
      {showBanner && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-[#0f1b2d] text-white shadow-xl" data-testid="guide-sticky-bar">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 justify-between">
            <div className="text-xs sm:text-sm">
              Get your <strong>{destName}</strong> visa documents prepared — starting at <strong>$39</strong>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/apply?destination=${encodeURIComponent(destName)}`}
                data-testid="guide-sticky-cta"
                className="inline-flex items-center gap-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-xs sm:text-sm font-semibold px-3 py-2 rounded-md"
              >
                Start now <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <button onClick={() => setShowBanner(false)} aria-label="Dismiss" className="text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}

// ---------- Merge helpers ----------
function mergeSection(base, override) {
  if (!override) return base || {};
  if (!base) return override;
  return { ...base, ...override };
}
function mergeDocuments(base, override) {
  const merged = { groups: [...((base || {}).groups || [])] };
  if (override?.groups) {
    for (const g of override.groups) merged.groups.push(g);
  }
  return merged;
}
function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}

// ---------- Tab sections ----------
function TabSectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 text-[#2563eb]">
        <Icon className="w-5 h-5" />
        <span className="text-xs uppercase tracking-widest font-semibold">{title}</span>
      </div>
      {subtitle && <h2 className="mt-1 font-display font-bold text-2xl text-[#0f1b2d]">{subtitle}</h2>}
    </div>
  );
}

function TabOverview({ id, data, destName, natName }) {
  return (
    <section id={id} className="scroll-mt-32" data-testid="tab-content-overview">
      <TabSectionHeader icon={Info} title="Overview" subtitle={`About the ${destName} Schengen visa`} />
      {data.text && (
        <div className="text-[#1e293b] leading-relaxed">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.text}</ReactMarkdown>
        </div>
      )}
      {data.bullets && data.bullets.length > 0 && (
        <ul className="mt-4 space-y-2">
          {data.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-[#1e293b]">
              <CheckCircle2 className="w-4 h-4 mt-1 text-[#16a34a] flex-shrink-0" /><span>{b}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TabFees({ id, data }) {
  const rows = data.rows || [];
  return (
    <section id={id} className="scroll-mt-32" data-testid="tab-content-fees">
      <TabSectionHeader icon={DollarSign} title="Visa Fees" subtitle="Fees and service charges" />
      <div className="overflow-x-auto border border-[#e2e8f0] rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-[#0f1b2d]">Category</th>
              <th className="text-left px-4 py-3 font-semibold text-[#0f1b2d]">Visa fee (EUR)</th>
              <th className="text-left px-4 py-3 font-semibold text-[#0f1b2d]">Approx. local</th>
              <th className="text-left px-4 py-3 font-semibold text-[#0f1b2d]">Service fee (EUR)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#f9fafb]"}>
                <td className="px-4 py-3 text-[#0f1b2d] font-medium">{r.category}</td>
                <td className="px-4 py-3">{r.eur === 0 ? "Free" : `€${r.eur}`}</td>
                <td className="px-4 py-3">{r.local || "—"}</td>
                <td className="px-4 py-3">{r.service_fee_eur ? `€${r.service_fee_eur}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.note && (
        <div className="mt-3 text-xs text-[#64748b] italic">{data.note}</div>
      )}
    </section>
  );
}

function TabDocuments({ id, data, destName }) {
  const groups = data.groups || [];
  return (
    <section id={id} className="scroll-mt-32" data-testid="tab-content-documents">
      <TabSectionHeader icon={FileText} title="Documents" subtitle="Required documents checklist" />
      <div className="space-y-6">
        {groups.map((g, i) => (
          <div key={i}>
            <h3 className="font-display font-bold text-lg text-[#0f1b2d] mb-2">{g.title}</h3>
            <div className="space-y-2">
              {(g.items || []).map((it, j) => (
                <details key={j} className="border border-[#e2e8f0] rounded-lg bg-white group">
                  <summary className="cursor-pointer list-none px-4 py-3 flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#2563eb] mt-1 flex-shrink-0" />
                    <span className="text-sm font-medium text-[#0f1b2d] flex-1">{it.label}</span>
                    {it.detail && <span className="text-xs text-[#64748b] group-open:rotate-180 transition">▾</span>}
                  </summary>
                  {it.detail && <div className="px-4 pb-4 text-sm text-[#475569] leading-relaxed pl-11">{it.detail}</div>}
                </details>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* CTA card */}
      <div className="mt-8 rounded-xl border-2 border-[#2563eb]/30 bg-gradient-to-br from-[#eff6ff] to-white p-5 sm:p-6" data-testid="guide-docs-cta">
        <div className="text-xs uppercase tracking-widest text-[#2563eb] font-semibold">Need help preparing these?</div>
        <h4 className="mt-1 font-display font-bold text-xl text-[#0f1b2d]">
          SchengenFile prepares consulate-ready documents in 24 hours
        </h4>
        <p className="mt-2 text-sm text-[#475569]">
          Cover letters, flight PNRs, hotel bookings, insurance and more — professionally formatted for the {destName} consulate.
        </p>
        <Link
          to={`/apply?destination=${encodeURIComponent(destName)}`}
          data-testid="guide-docs-cta-apply"
          className="mt-4 inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold px-5 py-2.5 rounded-md text-sm transition"
        >
          Prepare my documents <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

function TabPhoto({ id, data }) {
  return (
    <section id={id} className="scroll-mt-32" data-testid="tab-content-photo">
      <TabSectionHeader icon={Camera} title="Photo Specifications" subtitle="Schengen visa photo requirements" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <ul className="space-y-2">
          {(data.specs || []).map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-[#1e293b] text-sm">
              <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#16a34a] flex-shrink-0" /><span>{s}</span>
            </li>
          ))}
        </ul>
        <div className="rounded-lg border-2 border-dashed border-[#cbd5e1] bg-[#f8fafc] p-6 text-center">
          <div className="mx-auto w-28 h-36 rounded-md bg-white border border-[#e2e8f0] grid place-items-center">
            <div className="w-14 h-14 rounded-full bg-[#e2e8f0]" />
          </div>
          <div className="mt-3 text-xs text-[#64748b]">Example — face centered, neutral background</div>
        </div>
      </div>
    </section>
  );
}

function TabProcessing({ id, data }) {
  return (
    <section id={id} className="scroll-mt-32" data-testid="tab-content-processing">
      <TabSectionHeader icon={Clock} title="Processing Time" subtitle="How long will it take?" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border border-[#e2e8f0] bg-white p-4">
          <div className="text-xs uppercase text-[#64748b] tracking-wider">Standard</div>
          <div className="mt-1 font-display font-bold text-2xl text-[#0f1b2d]">{data.standard_days || "15 working days"}</div>
        </div>
        <div className="rounded-lg border border-[#fcd34d] bg-[#fefce8] p-4">
          <div className="text-xs uppercase text-[#78350f] tracking-wider">Peak season</div>
          <div className="mt-1 text-sm text-[#78350f] leading-relaxed">{data.peak_note}</div>
        </div>
        <div className="rounded-lg border border-[#93c5fd] bg-[#eff6ff] p-4">
          <div className="text-xs uppercase text-[#1e3a8a] tracking-wider">Application window</div>
          <div className="mt-1 text-sm text-[#1e3a8a] leading-relaxed">{data.window_note}</div>
        </div>
      </div>
    </section>
  );
}

function TabWhereToApply({ id, data, appointmentUrl, residenceEntry, residenceName, destinationName, residenceSelected }) {
  // If the current residence has a dedicated entry, use it. Otherwise, if
  // a residence IS selected but there's no matching entry, show a generic
  // fallback card — NEVER surface another country's provider/cities (bug
  // fix: "residence=Saudi Arabia was showing India cities").
  const useResidence = !!residenceEntry;
  const showGenericFallback = !useResidence && residenceSelected;

  const effective = useResidence ? {
    provider: residenceEntry.provider,
    cities: residenceEntry.cities || [],
    notes: residenceEntry.service_fee_note,
    appointment_url: residenceEntry.booking_url,
  } : (showGenericFallback ? null : data);
  const bookUrl = (residenceEntry && residenceEntry.booking_url) || appointmentUrl || (data && data.appointment_url);

  return (
    <section id={id} className="scroll-mt-32" data-testid="tab-content-where_to_apply">
      <TabSectionHeader
        icon={MapPin}
        title="Where to Apply"
        subtitle={useResidence && residenceName ? `Booking your appointment from ${residenceName}` : "Booking your appointment"}
      />
      <div className="rounded-lg border border-[#e2e8f0] bg-white p-5" data-testid="guide-wta-card">
        {useResidence && (
          <div className="mb-3 text-xs inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-1 rounded font-semibold" data-testid="guide-wta-residence-badge">
            <MapPin className="w-3 h-3" /> Personalised for {residenceName || residenceEntry.residence_country_name || residenceEntry.residence_country_code}
          </div>
        )}
        {showGenericFallback ? (
          <div data-testid="guide-wta-generic-fallback">
            <div className="mb-3 text-xs inline-flex items-center gap-1.5 bg-blue-50 text-blue-900 border border-blue-200 px-2 py-1 rounded font-semibold">
              <Info className="w-3 h-3" /> Country-specific centre not listed
            </div>
            <div className="text-sm text-[#0f1b2d] leading-relaxed">
              Official visa application centre or consulate of <strong>{destinationName}</strong> in <strong>{residenceName || "your country"}</strong> — exact provider and cities vary; check the <strong>{destinationName}</strong> consulate&apos;s official page for the centre serving your residence.
            </div>
          </div>
        ) : (
          <>
            <div className="text-xs uppercase text-[#64748b] tracking-wider font-semibold">Service provider</div>
            <div className="mt-1 font-display font-bold text-lg text-[#0f1b2d]" data-testid="guide-wta-provider">{(effective && effective.provider) || "—"}</div>
            {effective && effective.cities && effective.cities.length > 0 && (
              <div className="mt-4">
                <div className="text-xs uppercase text-[#64748b] tracking-wider font-semibold">Available cities</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {effective.cities.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 text-xs bg-[#f1f5f9] text-[#0f1b2d] px-2 py-1 rounded">
                      <MapPin className="w-3 h-3" /> {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {effective && effective.notes && <div className="mt-4 text-sm text-[#475569]">{effective.notes}</div>}
            {bookUrl && (
              <a
                href={bookUrl}
                target="_blank" rel="noopener noreferrer"
                data-testid="guide-appointment-link"
                className="mt-4 inline-flex items-center gap-2 border-2 border-[#2563eb] text-[#2563eb] hover:bg-[#eff6ff] font-semibold px-4 py-2 rounded-md text-sm"
              >
                Book appointment <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </>
        )}
      </div>
    </section>
  );
}
